import { useDetailsDrawer } from '@src/components/DetailsDrawer/useDetailsDrawer';
import { useIsMobileLayout } from '@src/hooks/useIsMobileLayout';
import { LAYOUT_HEADER_HEIGHT } from '@src/styles/dimensions';
import {
  Box,
  Button,
  Flex,
  FormControlSizes,
  Icon,
  IconName,
  isMultileg,
  isOrderRejected,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  type Order,
  Popover,
  PopperHeader,
  useMixpanel,
  usePopoverState,
  useSecurity,
  useStrategiesContext,
} from '@talos/kyoko';
import type { ICellRendererParams } from 'ag-grid-community';
import { type MouseEvent, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTradingOrderDetailsRoute } from '../../containers/Routes/routes';
import { useCurrentAppLayoutId } from '../../hooks/useAppLayoutId';
import { useLatestOrderAnalytics } from '../../hooks/useLatestOrderAnalytics';
import { OrderCardHeader } from '../OMS/OMSCard/OrderCardHeader';
import { TradePredictionsWrapper } from '../TradePredictions/TradePredictionsWrapper';
import { KeyMetricsCard } from './KeyMetricsCard';
import { TradingCard } from './TradingCard';

const TOOLTIP_WIDTH = '520px';
const TOOLTIP_HEIGHT = '450px';

function OrderAnalyticsTooltip({
  order,
  close,
  onHeightChange,
}: {
  order: Order;
  close: () => void;
  onHeightChange?: () => void;
}) {
  const mixpanel = useMixpanel();
  const security = useSecurity(order?.Symbol);
  const { strategiesByName } = useStrategiesContext();
  const selectedStrategy = order.Strategy ? strategiesByName?.get(order.Strategy) : undefined;
  const isOrderMultiLeg = isMultileg(security);
  const analytics = useLatestOrderAnalytics({
    order: order,
    tag: 'OrderAnalyticsColumn',
  });

  const navigate = useNavigate();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const previousHeightRef = useRef<number>(0);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const layoutId = useCurrentAppLayoutId();

  useEffect(() => {
    if (!tooltipRef.current || !onHeightChange) {
      return;
    }

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) {
        return;
      }

      const currentHeight = entry.contentRect.height;
      const previousHeight = previousHeightRef.current;

      if (Math.abs(currentHeight - previousHeight) > 1) {
        previousHeightRef.current = currentHeight;

        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }

        updateTimeoutRef.current = setTimeout(() => {
          onHeightChange();
        }, 16);
      }
    });

    resizeObserver.observe(tooltipRef.current);

    return () => {
      resizeObserver.disconnect();
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [onHeightChange]);

  const handleOpenAnalytics = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      close();
      mixpanel.track(MixpanelEvent.ViewFullAnalytics, {
        [MixpanelEventProperty.Source]: MixpanelEventSource.AnalyticsModal,
      });
      navigate(
        getTradingOrderDetailsRoute({
          orderID: order.OrderID,
          type: 'order',
          tab: 'analytics',
          layoutId,
        })
      );
    },
    [navigate, order.OrderID, close, mixpanel, layoutId]
  );

  return (
    <Flex
      ref={tooltipRef}
      flexDirection="column"
      gap="spacing8"
      w={TOOLTIP_WIDTH}
      minHeight={TOOLTIP_HEIGHT}
      maxHeight={`calc(100vh - ${LAYOUT_HEADER_HEIGHT * 2}px)`}
      overflow="auto"
      position="relative"
      data-testid="order-analytics"
    >
      <PopperHeader
        icon={IconName.ChartLine}
        title="Analytics"
        suffix={
          <Button startIcon={IconName.Exit} size={FormControlSizes.Small} onClick={handleOpenAnalytics}>
            View Full Analytics
          </Button>
        }
        onCloseClicked={close}
      />
      <Flex flexDirection="column" p="spacing8" gap="spacing4" flex="1 1 auto">
        <OrderCardHeader order={order} selectedStrategy={selectedStrategy} />
        <TradePredictionsWrapper
          order={order}
          isOrderMultiLeg={isOrderMultiLeg}
          latestAnalytics={analytics.latestAnalytics}
        />
        <KeyMetricsCard isMultileg={isOrderMultiLeg} order={order} analytics={analytics} />
        <TradingCard order={order} isMultileg={isOrderMultiLeg} analytics={analytics} />
      </Flex>
    </Flex>
  );
}

function DesktopOrderAnalyticsPopover({ order }: { order: Order }) {
  const mixpanel = useMixpanel();
  const popover = usePopoverState({
    trigger: 'hover',
    usePortal: true,
    noPaddingAndBorder: true,
    placement: 'right',
    delay: 500,
    closeOnClickOutside: true,
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['left'],
        },
      },
      {
        name: 'maxHeight',
        enabled: false,
      },
      {
        name: 'preventOverflow',
        enabled: true,
      },
    ],
    onOpen: () => {
      mixpanel.track(MixpanelEvent.ViewAnalyticsTooltip, {
        [MixpanelEventProperty.Source]: MixpanelEventSource.OrdersBlotter,
      });
    },
  });

  const handleHeightChange = useCallback(() => {
    if (popover.update) {
      popover.update();
    }
  }, [popover]);

  return (
    <Popover {...popover}>
      <Icon icon={IconName.ChartLine} />
      <OrderAnalyticsTooltip order={order} close={popover.close} onHeightChange={handleHeightChange} />
    </Popover>
  );
}

function MobileOrderAnalyticsTrigger({ order }: { order: Order }) {
  const mixpanel = useMixpanel();
  const { openDetailsDrawer } = useDetailsDrawer();

  const openFullAnalytics = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      mixpanel.track(MixpanelEvent.OpenOrderAnalytics);
      openDetailsDrawer({ entity: order });
    },
    [mixpanel, openDetailsDrawer, order]
  );

  return (
    <Box
      onClick={openFullAnalytics}
      data-testid="order-analytics-mobile-trigger"
      cursor="pointer"
      display="inline-flex"
    >
      <Icon icon={IconName.ChartLine} />
    </Box>
  );
}

export function OrderAnalyticsTooltipCellRenderer({ data }: ICellRendererParams<Order>) {
  const isMobileLayout = useIsMobileLayout();

  if (!data || !data.OrderID || isOrderRejected(data.OrdStatus)) {
    return null;
  }

  if (isMobileLayout) {
    return <MobileOrderAnalyticsTrigger order={data} />;
  }

  return <DesktopOrderAnalyticsPopover order={data} />;
}
